import { Container, FederatedPointerEvent, Graphics } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { PLAYER } from '@/config/player';
import { TOUCH_CUES as CUE } from '@/config/touch';
import type { TouchState } from '@/input/touch';
import type { Player } from '@/entities/player';

const STICK_RADIUS = 64;
const KNOB_RADIUS = 26;
const BTN_RADIUS = 42;
const TAU = Math.PI * 2;
const TOP = -Math.PI / 2;

/** DASH / SUPER button with a cooldown sweep + ready pulse (docs/06). */
interface CueButton {
  root: Container;
  icon: Graphics;
  /** Pre-built sweep + ring per charge step — fights only toggle visibility */
  frames: Graphics[];
  halo: Graphics;
  /** Visible sweep step (-1 = none) */
  step: number;
  ready: boolean;
  /** Seconds left of the became-ready pop */
  pop: number;
}

/**
 * Mobile overlay: dynamic virtual stick (left half) + DASH/SUPER buttons
 * (bottom right) + pause gear (top right). Visible only while the active
 * device is touch. Minimum hit areas exceed 48px (docs/06).
 */
export class TouchUi {
  readonly view = new Container();
  private readonly stickZone = new Graphics();
  private readonly stickBase = new Graphics();
  private readonly stickKnob = new Graphics();
  private stickPointer: number | null = null;
  private originX = 0;
  private originY = 0;
  private screenW = 0;
  private screenH = 0;

  constructor(private readonly touch: TouchState) {
    this.view.visible = false;

    // Invisible capture zone — left 55% of the screen spawns the stick
    this.stickZone.eventMode = 'static';
    this.view.addChild(this.stickZone);

    this.drawStick();
    this.stickBase.visible = this.stickKnob.visible = false;
    this.view.addChild(this.stickBase, this.stickKnob);

    this.stickZone.on('pointerdown', (e: FederatedPointerEvent) => {
      if (this.stickPointer !== null) return;
      this.stickPointer = e.pointerId;
      this.originX = e.globalX;
      this.originY = e.globalY;
      this.stickBase.position.set(this.originX, this.originY);
      this.stickKnob.position.set(this.originX, this.originY);
      this.stickBase.visible = this.stickKnob.visible = true;
      this.touch.noteActivity();
    });
    window.addEventListener('pointermove', (e) => this.onMove(e));
    window.addEventListener('pointerup', (e) => this.onUp(e));
    window.addEventListener('pointercancel', (e) => this.onUp(e));

    this.buildButtons();
  }

  private drawStick(): void {
    this.stickBase
      .circle(0, 0, STICK_RADIUS)
      .fill({ color: 0x221c30, alpha: 0.4 })
      .stroke({ color: COLORS.uiAccent, width: 3, alpha: 0.6 });
    this.stickKnob
      .circle(0, 0, KNOB_RADIUS)
      .fill({ color: COLORS.uiAccent, alpha: 0.55 });
  }

  private onMove(e: PointerEvent): void {
    if (e.pointerId !== this.stickPointer) return;
    let dx = e.clientX - this.originX;
    let dy = e.clientY - this.originY;
    const len = Math.hypot(dx, dy);
    if (len > STICK_RADIUS) {
      dx = (dx / len) * STICK_RADIUS;
      dy = (dy / len) * STICK_RADIUS;
    }
    this.stickKnob.position.set(this.originX + dx, this.originY + dy);
    this.touch.moveX = dx / STICK_RADIUS;
    this.touch.moveY = dy / STICK_RADIUS;
    this.touch.noteActivity();
  }

  private onUp(e: PointerEvent): void {
    if (e.pointerId !== this.stickPointer) return;
    this.stickPointer = null;
    this.touch.moveX = 0;
    this.touch.moveY = 0;
    this.stickBase.visible = this.stickKnob.visible = false;
  }

  // ---- Action buttons ----

  private dashCue!: CueButton;
  private superCue!: CueButton;
  private pauseBtn!: Container;
  private lastSync = 0;

  private makeButton(draw: (g: Graphics) => void): Container {
    const c = new Container();
    const bg = new Graphics()
      .circle(0, 0, BTN_RADIUS)
      .fill({ color: 0x221c30, alpha: 0.5 })
      .stroke({ color: COLORS.uiAccent, width: 3, alpha: 0.7 });
    const icon = new Graphics();
    draw(icon);
    c.addChild(bg, icon);
    c.eventMode = 'static';
    return c;
  }

  /**
   * Action button + its readiness layers. Every charge step's sweep (dark
   * sector over the part still charging) + progress ring is drawn ONCE here,
   * so a fight never rebuilds geometry (no allocation, CLAUDE.md #5).
   */
  private makeCueButton(draw: (g: Graphics) => void, color: number): CueButton {
    const root = this.makeButton(draw);
    const icon = root.children[1] as Graphics;
    const frames: Graphics[] = [];
    for (let k = 0; k < CUE.sweepSteps; k++) {
      const end = TOP + (k / CUE.sweepSteps) * TAU;
      const g = new Graphics();
      g.moveTo(0, 0).arc(0, 0, BTN_RADIUS - 1, end, TOP + TAU).closePath()
        .fill({ color: CUE.sweepColor, alpha: CUE.sweepAlpha });
      if (k > 0) {
        g.arc(0, 0, BTN_RADIUS + CUE.ringGap, TOP, end)
          .stroke({ color, width: CUE.ringWidth, cap: 'round' });
      }
      g.visible = false;
      frames.push(g);
    }
    const halo = new Graphics()
      .circle(0, 0, BTN_RADIUS + CUE.haloGap)
      .stroke({ color, width: CUE.haloWidth });
    halo.visible = false;
    root.addChild(...frames, halo);
    return { root, icon, frames, halo, step: -1, ready: false, pop: 0 };
  }

  /**
   * Per render frame: dash recharge + supershot energy → cooldown sweep
   * (shows the pre-built frame of the current step) and the ready pulse.
   */
  sync(player: Player): void {
    const now = performance.now() / 1000;
    const dt = this.lastSync > 0 ? Math.min(0.1, now - this.lastSync) : 0;
    this.lastSync = now;
    if (!this.view.visible) return;
    this.cue(this.dashCue, player.dashReadyPct, now, dt);
    this.cue(this.superCue, player.energy / PLAYER.maxEnergy, now, dt);
  }

  private cue(b: CueButton, pct: number, now: number, dt: number): void {
    const charged = Math.max(0, Math.min(1, pct));
    const ready = charged >= 1;
    const step = ready ? -1 : Math.min(CUE.sweepSteps - 1, Math.floor(charged * CUE.sweepSteps));
    if (step !== b.step) {
      if (b.step >= 0) b.frames[b.step]!.visible = false;
      if (step >= 0) b.frames[step]!.visible = true;
      b.step = step;
    }
    if (ready && !b.ready) b.pop = CUE.readyPopTime;
    b.ready = ready;
    b.icon.alpha = ready ? 1 : CUE.dimIconAlpha;
    b.halo.visible = ready;
    b.pop = Math.max(0, b.pop - dt);
    b.root.scale.set(1 + CUE.readyPopScale * (b.pop / CUE.readyPopTime));
    if (ready) {
      const wave = 0.5 + 0.5 * Math.sin(now * CUE.pulseSpeed);
      b.halo.alpha = CUE.pulseMinAlpha + (1 - CUE.pulseMinAlpha) * wave;
      b.halo.scale.set(1 + CUE.pulseScale * wave);
    }
  }

  private buildButtons(): void {
    // DASH — wing arrow
    this.dashCue = this.makeCueButton((g) => {
      g.poly([-14, -12, 16, 0, -14, 12, -6, 0]).fill({ color: 0xffffff, alpha: 0.9 });
    }, CUE.dashColor);
    this.dashCue.root.on('pointerdown', () => this.touch.queueDash());

    // SUPERSHOT — star, hold to fire when charged
    this.superCue = this.makeCueButton((g) => {
      for (let i = 0; i < 5; i++) {
        const a = (Math.PI * 2 * i) / 5 - Math.PI / 2;
        g.poly([
          Math.cos(a) * 16, Math.sin(a) * 16,
          Math.cos(a + 0.45) * 6.5, Math.sin(a + 0.45) * 6.5,
          Math.cos(a - 0.45) * 6.5, Math.sin(a - 0.45) * 6.5,
        ]).fill({ color: COLORS.playerCyan, alpha: 0.95 });
      }
    }, CUE.superColor);
    this.superCue.root.on('pointerdown', () => {
      this.touch.superHeld = true;
      this.touch.noteActivity();
    });
    const releaseSuper = (): void => {
      this.touch.superHeld = false;
    };
    this.superCue.root.on('pointerup', releaseSuper);
    this.superCue.root.on('pointerupoutside', releaseSuper);

    // PAUSE — two bars
    this.pauseBtn = this.makeButton((g) => {
      g.roundRect(-9, -12, 7, 24, 3).fill({ color: 0xffffff, alpha: 0.9 });
      g.roundRect(2, -12, 7, 24, 3).fill({ color: 0xffffff, alpha: 0.9 });
    });
    this.pauseBtn.scale.set(0.7);
    this.pauseBtn.on('pointerdown', () => this.touch.queuePause());

    this.view.addChild(this.dashCue.root, this.superCue.root, this.pauseBtn);
  }

  layout(screenW: number, screenH: number): void {
    if (screenW === this.screenW && screenH === this.screenH) return;
    this.screenW = screenW;
    this.screenH = screenH;

    this.stickZone
      .clear()
      .rect(0, 0, screenW * 0.55, screenH)
      .fill({ color: 0xffffff, alpha: 0.0001 });

    this.superCue.root.position.set(screenW - 80, screenH - 90);
    this.dashCue.root.position.set(screenW - 190, screenH - 140);
    this.pauseBtn.position.set(screenW - 56, 56);
  }

  setVisible(visible: boolean): void {
    this.view.visible = visible;
    // Hidden mid-hold (an overlay opened): no stuck supershot afterwards
    if (!visible) this.touch.superHeld = false;
  }
}
